import { useAuth0 } from '@auth0/auth0-react';
import { decodeTokenPayload, firstPasswordHint, PASSWORD_POLICY_HINT } from '@zealot/shared';
import { fanUsersRegister } from '@zealot/shared/apiClient';
import { AnimatePresence, motion } from 'framer-motion';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef, useState } from 'react';

import AuthLayout from '@/components/auth/AuthLayout';
import BrandPicker from '@/components/brand/BrandPicker';
import { TextureButton } from '@/components/ui/TextureButton';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useBrand } from '@/contexts/brand';
import { useBrandCodeLookup } from '@/hooks/useBrandCodeLookup';
import { useSignedInRedirect } from '@/hooks/useSignedInRedirect';
import { useSignInSuppressed } from '@/hooks/useSignInSuppressed';
import { brandHintFromUrl } from '@/lib/appBootstrap/brandHintFromUrl';
import { assertFanUserNotBanned, FanUserBannedError } from '@/lib/auth/assertFanUserNotBanned';
import { clearJustLoggedOutFlag } from '@/lib/auth/clearSessionState';
import { classifyCallback, IDP_RETRY_KEY } from '@/lib/auth/idpInitiatedCallback';
import { isMobileViewport } from '@/lib/utils/isMobileViewport';
import { navigateWithBrand } from '@/lib/utils/navigation';
import { isFirstTimeSignUp } from '@/lib/utils/onboarding';
import { useAuth } from '@/pages/AuthContext';
import { getBrandDetails } from '@/pages/api/brand-config';
import { getBrandIDByName } from '@/pages/api/brand-missions';
import { listProfile } from '@/pages/api/fan-profile';
import { getHubBrand, getHubBrandName, getStoredBrandTheme, useHubBrand } from '@/store';
import { API_BASE_URL } from '@/utils/api';
import { getApiToken, getValidAuthToken, setMagicToken } from '@/utils/auth';
import logger from '@/utils/logger';
import { shouldShowSignup } from '@/utils/shouldShowSignup';

// Decodes the Auth0 idToken JWT payload and persists the user's identity to
// localStorage (userUUID + userID). Returns false when the token can't be
// decoded so callers can surface a session error. The home guard and
// Onboarding both rely on `userID` being set after any successful auth — a
// missing one bounces the user back to login in a redirect loop.
const persistIdentityFromIdToken = (idToken, fallbackEmail) => {
  const userInfo = decodeTokenPayload(idToken);
  if (!userInfo || typeof userInfo.sub !== 'string') {
    logger.error(new Error('Failed to decode idToken'), { context: 'Error decoding idToken' });
    return false;
  }
  localStorage.setItem('userUUID', userInfo.sub);
  localStorage.setItem(
    'userID',
    (typeof userInfo.email === 'string' && userInfo.email) || fallbackEmail || userInfo.sub,
  );
  return true;
};

const Index = () => {
  const { isAuthenticated, isLoading: auth0IsLoading, user, getIdTokenClaims } = useAuth0();
  const {
    loginWithSSO,
    fetchToken,
    logout,
    authCredentialsAvailable,
    startDbLogin,
    startDbSignup,
    loginWithEmailAndPassword,
  } = useAuth();

  const apiClient = useApiClient();
  const lookupBrandCode = useBrandCodeLookup();

  // Provision the FanUser via the Hono API (POST /api/v2/fanUsers/register),
  // replacing the legacy /register-user/add call. Goes through the
  // ApiClientContext client so its response interceptor maps signup-guard
  // outcomes (USER_BANNED / SIGNUP_PENDING_REVIEW / APPLICATION_REJECTED /
  // SIGNUP_UNAVAILABLE) to the SignupStatusScreen. The Auth0 token is attached
  // by that client's request interceptor — callers no longer pass it.
  //
  // Returns { ok, blocked }: on a block (403/503) the screen is already
  // showing, so callers must NOT logout or redirect — just stop.
  const registerFanUser = useCallback(
    async ({ userID, email, password, phoneNumber, picture, firstName, lastName, brandCode, inviteTag }) => {
      try {
        // `password` present → the BE creates the Auth0 user (management API,
        // password branch) and runs guards pre-Auth0. Absent → SSO/magic/OTP
        // provisioning where the Auth0 identity already exists.
        const { data, error, response } = await fanUsersRegister({
          client: apiClient,
          body: {
            email: email || userID,
            password: password || undefined,
            firstName: firstName || undefined,
            lastName: lastName || undefined,
            picture: picture || undefined,
            phoneNumber: phoneNumber || undefined,
            brandCode,
            inviteTag: inviteTag || undefined,
          },
        });
        const status = response?.status ?? 0;
        const blocked = status === 403 || status === 503;
        // Surface the BE's message so callers can show the real reason (e.g. the
        // 409 "already registered" copy) instead of a generic failure string.
        const message = typeof error?.message === 'string' ? error.message : null;
        return { ok: !!data, blocked, status, message };
      } catch (err) {
        logger.error('Fan user registration failed', { err });
        return { ok: false, blocked: false, status: 0, message: null };
      }
    },
    [apiClient],
  );

  useEffect(() => {
    const readNormalizedClaims = async () => {
      if (!isAuthenticated) return;
      try {
        const claims = await getIdTokenClaims();
        const ns = 'https://joinzealot.com/claims';
        const normalizedProfile = {
          email: claims?.[`${ns}/email`] || claims?.email || user?.email || null,
          givenName: claims?.[`${ns}/given_name`] || claims?.given_name || user?.given_name || null,
          familyName: claims?.[`${ns}/family_name`] || claims?.family_name || user?.family_name || null,
          name: claims?.[`${ns}/name`] || claims?.name || user?.name || null,
          tenantId: claims?.[`${ns}/tenantid`] || null,
          objectId: claims?.[`${ns}/objectidentifier`] || null,
          identityProvider: claims?.[`${ns}/identityprovider`] || null,
          authenticationMethod: claims?.[`${ns}/authenticationmethod`] || null,
          emailIsSynthetic: claims?.[`${ns}/email_is_synthetic`] === true || false,
        };
        setNormalizedProfile(normalizedProfile);

        const stableIdentifier =
          (normalizedProfile.email && !normalizedProfile.emailIsSynthetic ? normalizedProfile.email : null) ||
          user?.email ||
          user?.sub ||
          null;
        if (stableIdentifier) {
          localStorage.setItem('userID', stableIdentifier);
        }
      } catch (e) {
        logger.error(e, { context: '[Index] Failed to read ID token claims' });
      }
    };
    readNormalizedClaims();
  }, [isAuthenticated, getIdTokenClaims, user]);

  const router = useRouter();

  // Use BrandContext for brand theme data
  const { brand, isLoading: brandIsLoading } = useBrand();
  const primarycolor = brand?.brandConfig?.primaryColor ?? '#6366f1';
  const brandLogo = brand?.brandConfig?.colorBrandLogo ?? brand?.brandConfig?.icon ?? '';

  const loginBackground = brand?.brandConfig?.loginBackground ?? null;
  const loaderGifUrl = brand?.brandConfig?.loaderGIFURL ?? null;

  const [param, setParam] = useState('');
  const [_secondaryColor, setSecondaryColor] = useState('');
  const [favicon, setFavicon] = useState('');
  const [brandTitle, setBrandTitle] = useState('');
  const [isUserRegistrationComplete, setIsUserRegistrationComplete] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [userID, setUserID] = useState();
  const [brandName, setBrandName] = useState('');
  const hubBrandName = useHubBrand().brandName ?? null;
  const [_phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [_requested, _setRequested] = useState(false);
  const [_currentStep, _setCurrentStep] = useState(0);
  const [_backgroundColor, setBackgroundColor] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [authAttempted, setAuthAttempted] = useState(false);
  const [ssoNotConfigured, setSsoNotConfigured] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  const [brandNameDetermined, setBrandNameDetermined] = useState(false);
  const [brandAssetsReady, setBrandAssetsReady] = useState(false);
  const [isSSOBrand, setIsSSOBrand] = useState(false);
  const [isSSOCheckComplete, setIsSSOCheckComplete] = useState(false);
  const [normalizedProfile, setNormalizedProfile] = useState(null);
  const [email, setEmail] = useState(() => {
    // Prefill email from localStorage if available (e.g., after signup)
    if (typeof window !== 'undefined') {
      return localStorage.getItem('signupEmail') || '';
    }
    return '';
  });
  const [isSignUp, setIsSignUp] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginInProgress, setLoginInProgress] = useState(false);

  // Ref to prevent repeated fetchDetails calls when token is unavailable
  const fetchDetailsAttemptedRef = useRef(false);

  const { justLoggedOut, suppressed: signInSuppressed } = useSignInSuppressed();

  useSignedInRedirect();

  // Default to the sign-up form for signup/mode params or segment invite links,
  // and prefill email from the URL param when provided.
  useEffect(() => {
    if (router.isReady) {
      if (shouldShowSignup(router.query)) {
        setIsSignUp(true);
      }
      // Prefill email from URL param if provided (e.g., ?email=user@example.com)
      const emailParam = router.query.email;
      if (emailParam && typeof emailParam === 'string') {
        setEmail(decodeURIComponent(emailParam));
      }
    }
  }, [router.isReady, router.query.signup, router.query.mode, router.query.inviteTag, router.query.email]);

  // Determine if current brand has SSO configured.
  // Primary source: BrandContext (uses /api/v2/brands/lookup which matches by code).
  // Fallback: legacy endpoint (matches by brandTitle — can fail when brandTitle is empty).
  useEffect(() => {
    setIsSSOBrand(false);
    setIsSSOCheckComplete(false);

    if (!brandName) {
      setIsSSOCheckComplete(true);
      return;
    }
    if (router.query.embed === 'true') {
      setIsSSOCheckComplete(true);
      return;
    }

    // Check BrandContext first — it already contains ssoConnectionName from the new API
    if (brand?.brandConfig?.ssoConnectionName) {
      setIsSSOBrand(true);
      setIsSSOCheckComplete(true);
      return;
    }

    // If BrandContext is still loading, wait for it before falling back
    if (brandIsLoading) {
      return;
    }

    // BrandContext loaded but no ssoConnectionName — try the legacy endpoint as fallback
    let cancelled = false;
    const checkLegacy = async () => {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        const response = await fetch(`${API_BASE_URL}/brand-config/sso-connection/${encodeURIComponent(brandName)}`, {
          signal: controller.signal,
        });
        clearTimeout(timeoutId);
        if (cancelled) return;
        const data = await response.json();
        if (cancelled) return;
        const hasSSO = Array.isArray(data)
          ? data.length > 0 && !!data[0]?.ssoConnectionName
          : !!data?.ssoConnectionName;
        setIsSSOBrand(!!hasSSO);
      } catch (_e) {
        if (!cancelled) setIsSSOBrand(false);
      } finally {
        if (!cancelled) setIsSSOCheckComplete(true);
      }
    };

    checkLegacy();
    return () => {
      cancelled = true;
    };
  }, [brandName, router.query.embed, brand?.brandConfig?.ssoConnectionName, brandIsLoading]);

  useEffect(() => {
    let timer;
    if (isLoading || isAuthenticated) {
      timer = setTimeout(() => {
        // Cancel loading to allow UI to render login instead of staying blank
        setIsLoading(false);
        localStorage.removeItem('auth_in_progress');
        setShowResetButton(true);
      }, 7000);
    }
    return () => clearTimeout(timer);
  }, [isLoading, brandName, isAuthenticated]);

  const handleReset = () => {
    localStorage.clear();
    logout();
    router.push('/');
    window.location.reload();
  };

  useEffect(() => {
    const fetchDetails = async () => {
      if (!param || param === '') {
        setBrandAssetsReady(true); // No specific assets to load if no param
        return;
      }

      // Prevent repeated fetch attempts when auth is unavailable
      if (fetchDetailsAttemptedRef.current === param) {
        return;
      }

      setBrandAssetsReady(false); // Reset before fetching new assets

      try {
        const accessToken = await getApiToken(fetchToken);
        if (!accessToken) {
          // Mark as attempted to prevent re-running on fetchToken changes
          fetchDetailsAttemptedRef.current = param;
          // Do not block UI on brand assets; allow login page to render
          setBrandAssetsReady(true);
          return;
        }

        const val = await getBrandIDByName(param, accessToken);

        if (!val?.body?.[0]?.brandID) {
          fetchDetailsAttemptedRef.current = param;
          setBrandAssetsReady(true);
          return;
        }

        getHubBrand().updateHubBrand({ brandName: param });

        try {
          const details = await getBrandDetails(val?.body[0]?.brandID, accessToken);

          if (details?.body && details.body.length > 0) {
            setBrandTitle(details?.body[0]?.brandTitle);
            if (isMobileViewport()) {
              setBackgroundColor(true);
            }
            setSecondaryColor(details?.body[0]?.secondaryColor);
            setFavicon(details?.body[0]?.favicon || details?.body[0]?.colorBrandLogo);
            // Brand theme (primaryColor, brandLogo, loginBackground, loaderGifUrl) now handled by BrandContext

            setBrandAssetsReady(true); // Assets loaded successfully
          } else {
            getStoredBrandTheme().setBrandTheme({ loaderGIFURL: null });
            setBrandAssetsReady(true); // Attempt finished, no assets found
          }
        } catch (error) {
          logger.error(error, { context: '[fetchDetails] Error fetching brand details' });
          setBrandAssetsReady(true); // Attempt finished with error
        }
        // Mark as successfully attempted
        fetchDetailsAttemptedRef.current = param;
      } catch (error) {
        logger.error(error, { context: '[fetchDetails] Error in fetchDetails' });
        fetchDetailsAttemptedRef.current = param;
        setBrandAssetsReady(true); // Attempt finished with error
      }
    };

    // Reset ref if param changes (user navigates to different brand)
    if (fetchDetailsAttemptedRef.current && fetchDetailsAttemptedRef.current !== param) {
      fetchDetailsAttemptedRef.current = null;
    }

    // Add a small delay to ensure param is properly set
    if (param) {
      fetchDetails();
    } else {
      // If no param is set (e.g. initial load before brand is determined, or no brand in URL)
      // then brand-specific assets are not pending a fetch.
      setBrandAssetsReady(true);
    }
  }, [param, fetchToken]);

  useEffect(() => {
    if (router.isReady) {
      if (hubBrandName && !brandName) {
        setBrandName(hubBrandName);
        setParam(hubBrandName);
        // Ensure we don't get stuck in neutral loader
        setBrandNameDetermined(true);
        return;
      }

      if (brandName) {
        // Ensure brand determination completes even if brandName was set earlier
        setBrandNameDetermined(true);
        return;
      }

      let brandCode;

      // Handle clearCache parameter first
      const clearCacheMatch = router.asPath.match(/clearCache=([^&]*)/);
      if (clearCacheMatch) {
        const shouldClear = clearCacheMatch[1].toLowerCase() === 'true';
        if (shouldClear) {
          // Save inviteTag if it exists
          const existingInviteTag = localStorage.getItem('inviteTag');
          // Clear all localStorage
          localStorage.clear();
          // Restore inviteTag if it existed
          if (existingInviteTag) {
            localStorage.setItem('inviteTag', existingInviteTag);
          }
        }
      }

      brandCode =
        brandHintFromUrl({ url: new URL(window.location.href), hostname: window.location.hostname }) ?? undefined;

      if (brandCode) {
        getHubBrand().updateHubBrand({ brandName: brandCode });
        setParam(brandCode);
        setBrandName(brandCode);
      }

      const inviteTagMatch = router.asPath.match(/inviteTag=([^&]*)/);
      if (inviteTagMatch) {
        const inviteTag = decodeURIComponent(inviteTagMatch[1]);
        localStorage.setItem('inviteTag', inviteTag);
      }

      setBrandNameDetermined(true); // Brand determination process is complete
    }
  }, [router.asPath, router.isReady, brandName, hubBrandName]);

  useEffect(() => {
    const user = localStorage.getItem('userID');
    if (user && authCredentialsAvailable()) {
      setUserID(user);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (hubBrandName && !brandName) {
      setBrandName(hubBrandName);
      setParam(hubBrandName);
    }
  }, [brandName, hubBrandName]);

  useEffect(() => {
    if (isUserRegistrationComplete === true && user) {
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }
      if (needsOnboarding) {
        navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
      } else {
        navigateWithBrand(router, isMobileViewport() ? '/Home' : '/HomeDesktop');
      }
    }
  }, [isUserRegistrationComplete, user, needsOnboarding]);

  useEffect(() => {
    const registerAuthenticatedUser = async () => {
      // Only proceed if we have both authentication and user data
      if (!isAuthenticated || !user) {
        return;
      }

      setIsLoading(true);
      try {
        const accessToken = await getApiToken(fetchToken);
        if (!accessToken) {
          setIsLoading(false);
          return;
        }

        // Store the access token consistently
        localStorage.setItem('access_token', accessToken);

        const userID = getUserIdentifier(user);
        if (!userID) {
          throw new Error('Unable to determine user identifier');
        }

        localStorage.setItem('userID', userID);

        // Fetch profile info. Legacy /fan-profile/profile/:userID returns a
        // single object (or null when the user does not exist). Use userID
        // (the FanUsers PK) as the existence test — brandID gets shadowed to
        // null by the Groups LEFT JOIN's column collision when the user has
        // no group memberships.
        const pInfo = await listProfile(userID, accessToken);
        if (pInfo.body?.userID) {
          setNeedsOnboarding(isFirstTimeSignUp(pInfo));
          setIsUserRegistrationComplete(true);
          setIsLoading(false);
          return;
        }

        // Continue with registration if profile doesn't exist
        const resolvedBrand = getHubBrandName() || brandName || param;
        const inviteTag = localStorage.getItem('inviteTag');

        if (resolvedBrand) {
          if (!getHubBrand().brandName) {
            getHubBrand().updateHubBrand({ brandName: resolvedBrand });
          }
          const brandCode = await lookupBrandCode(resolvedBrand, accessToken);

          if (brandCode) {
            const reg = await registerFanUser({
              userID,
              email: user.email,
              phoneNumber: user.phone_number,
              picture: user.picture,
              firstName: user.given_name || brandTitle,
              lastName: user.family_name || '',
              brandCode,
              inviteTag,
            });

            if (reg.ok) {
              setNeedsOnboarding(true);
              setIsUserRegistrationComplete(true);
            }
          }
        }
      } catch (error) {
        logger.error(error, { context: 'Registration error' });
        // If token fetch fails due to expired session, clear auth state
        if (error.message?.includes('Login required') || error.message?.includes('token')) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('auth_in_progress');
        }
      } finally {
        setIsLoading(false);
      }
    };

    registerAuthenticatedUser();
  }, [isAuthenticated, user, fetchToken]);

  // Magic link auth: verify profile and redirect (magic tokens bypass Auth0 SDK)
  // Auth0's `user` object is never set for magic links, so we redirect directly here.
  useEffect(() => {
    const handleMagicLinkAuth = async () => {
      let authToken = getValidAuthToken();

      // Fallback: child effects fire before parent effects, so useUrlTokenHandler
      // in _app.jsx may not have stored the token yet. Grab it from the URL directly.
      if ((!authToken || authToken.source !== 'magic') && typeof window !== 'undefined') {
        const urlToken = new URLSearchParams(window.location.search).get('token');
        if (urlToken) {
          setMagicToken(urlToken);
          authToken = getValidAuthToken();
        }
      }

      if (!authToken || authToken.source !== 'magic') return;

      const storedUserID = localStorage.getItem('userID');
      if (!storedUserID) {
        setLoginError('Unable to complete magic link sign-in. Please sign in manually or check your browser settings.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const accessToken = authToken.token;
        const pInfo = await listProfile(storedUserID, accessToken);
        if (pInfo.body?.userID) {
          navigateWithBrand(router, isMobileViewport() ? '/Home' : '/HomeDesktop');
          return;
        }

        // Register if profile doesn't exist
        const storedBrand = getHubBrandName();
        const inviteTag = localStorage.getItem('inviteTag');
        if (storedBrand) {
          const brandCode = await lookupBrandCode(storedBrand, accessToken);
          if (brandCode) {
            // firstName/lastName omitted — the BE derives a name from the
            // email local-part (findOrCreateGuest.extractFirstName).
            const reg = await registerFanUser({
              userID: storedUserID,
              email: storedUserID,
              brandCode,
              inviteTag,
            });
            if (reg.ok) {
              navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
            } else if (!reg.blocked) {
              // Genuine failure (network / unexpected server error). A blocked
              // signup already shows the SignupStatusScreen, so only surface
              // feedback for the non-blocked case — otherwise the finally clears
              // the spinner and leaves the user stuck with no explanation.
              logger.error('[MagicLink] Fan user registration failed', { status: reg.status });
              setLoginError('We could not complete your sign-in. Please try again.');
            }
          }
        }
      } catch (error) {
        logger.error('[MagicLink] Registration error', { err: error });
      } finally {
        setIsLoading(false);
      }
    };

    handleMagicLinkAuth();
  }, []);

  const _formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);

    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }

    return phoneNumber;
  };

  const handleLoginClick = async () => {
    try {
      await startDbLogin();
    } catch (_e) {
      setLoginError('Login failed. Please try again.');
    }
  };

  const _handleSignupClick = async () => {
    setIsSignUp(true);
    setLoginError('');
  };

  // Returns true when the user was navigated away (success), false when we
  // stayed on this screen (validation/blocked/409/error). The caller uses this
  // to keep the submit spinner up through the route transition on success
  // instead of flipping the button back to idle mid-navigation.
  const handleEmailPasswordSignUp = async () => {
    setLoginError('');
    try {
      if (verificationCode !== confirmPassword) {
        setLoginError('Passwords do not match');
        return false;
      }

      // Enforce the full shared password policy client-side. The first failing
      // rule is shown inline under the password field, so just block here.
      if (firstPasswordHint(verificationCode)) {
        return false;
      }

      if (!email || !email.includes('@')) {
        setLoginError('Please enter a valid email address');
        return false;
      }

      const brandCode = brand?.code;
      if (!brandCode) {
        setLoginError('Unable to determine your community. Please reload and try again.');
        return false;
      }
      const inviteTag = typeof window !== 'undefined' ? localStorage.getItem('inviteTag') : null;

      // One call: the BE creates the Auth0 user (management API), runs signup
      // guards, and provisions the FanUser. firstName/lastName are collected
      // during onboarding. Replaces the old two-step Auth0-create-then-sign-in.
      const reg = await registerFanUser({ email, password: verificationCode, brandCode, inviteTag });

      if (reg.blocked) {
        // 403/503 → ApiClientContext interceptor already routed to
        // SignupStatusScreen; don't show an inline error.
        return false;
      }
      if (reg.status === 409) {
        // The Auth0 identity already exists. That is not a conflict: one credential is shared by
        // every hub, so anyone joining a second brand hits this on their way in. They just typed
        // the password, so use it — a successful grant proves they own the account, and the first
        // authenticated request provisions their FanUser for THIS brand.
        //
        // Only a failed grant is a real conflict: the address belongs to someone else, or they
        // mistyped. Then, and only then, send them to sign in or reset.
        try {
          localStorage.setItem('signupEmail', email);
          const { accessToken: existingAccessToken, idToken: existingIdToken } = await loginWithEmailAndPassword(
            email,
            verificationCode,
          );
          if (persistIdentityFromIdToken(existingIdToken, email)) {
            setConfirmPassword('');
            setVerificationCode('');
            // The credential is shared across hubs, so a 409 says nothing about whether this
            // person is new HERE. Ask their profile, the same way every other post-login path
            // does: an existing member of this brand goes home, only a new membership onboards.
            const pInfo = await listProfile(email, existingAccessToken);
            const needsOnboardingHere = !pInfo?.body?.userID || isFirstTimeSignUp(pInfo);
            if (needsOnboardingHere) {
              await navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
            } else {
              await navigateWithBrand(router, isMobileViewport() ? '/Home' : '/HomeDesktop');
            }
            return true;
          }
        } catch (_joinError) {
          // Fall through to the sign-in prompt below.
        }
        localStorage.removeItem('signupEmail');
        setLoginError('This email is already registered. Please sign in or reset your password.');
        setIsSignUp(false);
        return false;
      }
      if (!reg.ok) {
        // Prefer the backend's specific message over a generic failure.
        setLoginError(reg.message || 'Signup failed. Please try again.');
        return false;
      }

      // Auto-login via the password-realm grant, then head to onboarding.
      localStorage.setItem('signupEmail', email);
      const { idToken } = await loginWithEmailAndPassword(email, verificationCode);

      if (!persistIdentityFromIdToken(idToken, email)) {
        setLoginError('Session error. Please try again.');
        return false;
      }

      setConfirmPassword('');
      setVerificationCode('');
      await navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
      return true;
    } catch (e) {
      logger.error(e, { context: 'Signup error' });
      setLoginError(e.message || 'Signup failed. Please try again.');
      return false;
    }
  };

  const _handleSSOLogin = async () => {
    if (authAttempted || isAuthenticated || isLoading) {
      return;
    }

    const attemptInProgress = localStorage.getItem('attempt_in_progress');
    if (attemptInProgress) {
      return;
    }

    try {
      localStorage.setItem('attempt_in_progress', 'true');
      localStorage.setItem('auth_in_progress', 'true');
      setAuthAttempted(true);
      await loginWithSSO(brandName, { ssoConnectionName: brand?.brandConfig?.ssoConnectionName });
    } finally {
      localStorage.removeItem('attempt_in_progress');
    }
  };

  const getUserIdentifier = (user) => {
    // Prefer normalized, non-synthetic email if we have it
    if (normalizedProfile?.email && !normalizedProfile?.emailIsSynthetic) {
      return normalizedProfile.email;
    }
    if (user && typeof user === 'object') {
      if (user.email) {
        return user.email;
      }
      if (user.phoneNumber || user.phone_number) {
        return user.phoneNumber || user.phone_number;
      }
      if (user.sub) {
        return user.sub;
      }
    }
    logger.error(user, { context: '[getUserIdentifier] Unable to determine user identifier' });
    return null;
  };

  const _handleLogin = async () => {
    await handleLoginClick();
  };

  const _handleRequestNewCode = async () => {};

  const _handlePhoneNumberChange = (value) => {
    setPhoneNumber(value);
  };

  useEffect(() => {
    const handleSSOAuth = async () => {
      // Skip SSO auth if we're in embed mode
      if (router.query.embed === 'true') {
        return;
      }

      if (isLoading) return;

      // Wait for Auth0 to restore any existing session; acting while it loads
      // treats a returning member as logged-out and forces a redundant SSO redirect.
      if (auth0IsLoading) return;

      // Decide what an arriving ?code= means. SP-initiated callbacks are owned
      // and already exchanged by the Auth0 SDK; IdP-initiated landings arrive
      // with a code the SDK can't exchange (no matching transaction), so we
      // re-run SSO to mint an SDK-owned code+state against the existing Auth0
      // session. See lib/auth/idpInitiatedCallback.ts.
      const alreadyRetried = typeof window !== 'undefined' && window.sessionStorage.getItem(IDP_RETRY_KEY) === '1';
      const callbackDecision = classifyCallback({
        search: window.location.search,
        isAuthenticated,
        isSSOBrand,
        brandName,
        alreadyRetried,
      });

      if (callbackDecision === 'sdk-owned') {
        // SDK handled it; the success effect will navigate. Nothing to do.
        return;
      }

      if (callbackDecision === 'idp-initiated-exhausted') {
        // We re-triggered once and STILL landed on a stateless code — Auth0 could
        // not silently complete against the session. Stop, and do NOT loop: strip
        // the dead params and disable SSO auto-login for this page session, so the
        // shouldAutoLogin block below doesn't immediately fire another /authorize.
        const deadUrl = new URL(window.location.href);
        deadUrl.searchParams.delete('code');
        deadUrl.searchParams.delete('state');
        window.history.replaceState({}, '', deadUrl.toString());
        setSsoNotConfigured(true);
        return;
      }

      if (callbackDecision === 'idp-initiated-retry') {
        try {
          window.sessionStorage.setItem(IDP_RETRY_KEY, '1');
          // Strip the unusable code/state before re-initiating so the SDK's own
          // callback detection isn't confused by the stale IdP-initiated params.
          const cleanUrl = new URL(window.location.href);
          cleanUrl.searchParams.delete('code');
          cleanUrl.searchParams.delete('state');
          window.history.replaceState({}, '', cleanUrl.toString());

          setIsLoading(true);
          await loginWithSSO(brandName, { ssoConnectionName: brand?.brandConfig?.ssoConnectionName });
        } catch (error) {
          window.sessionStorage.removeItem(IDP_RETRY_KEY);
          setIsLoading(false);
          logger.error(error, { context: 'IdP-initiated SSO re-trigger' });
        }
        return;
      }

      // callbackDecision === 'not-callback'. If a code is still present it's one
      // we can't act on yet (brand/SSO state not resolved, or a non-SSO brand) —
      // preserve the original early-return so a stray code never falls through to
      // the auto-login / registration logic below. The effect re-runs when
      // isSSOBrand resolves and reclassifies the code to 'idp-initiated-retry'.
      if (window.location.search.includes('code=')) {
        return;
      }

      // Only wait for normalizedProfile if user.email is not available from Auth0 user object
      // This handles SAML SSO users where email is only in ID token claims, not user object
      // If user.email exists, we can proceed immediately (most common case)
      if (isAuthenticated && user && !normalizedProfile && !user.email) {
        return;
      }

      // Try to fetch token if we're authenticated but don't have one
      if (isAuthenticated && user && !localStorage.getItem('access_token')) {
        try {
          // IMPORTANT: require an authenticated token; do not fall back to unauthenticated
          const token = await fetchToken();
          if (!token) {
            try {
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('force_interactive_login', '1');
              }
            } catch (_) {
              /* intentionally ignore sessionStorage access errors */
            }
            setIsLoading(true);
            await loginWithSSO(brandName, { ssoConnectionName: brand?.brandConfig?.ssoConnectionName });
            return;
          }

          // After getting token, check if user exists in fanusers
          const userID = getUserIdentifier(user);
          let profileInfo;
          try {
            profileInfo = await listProfile(userID, token);
          } catch (_error) {
            // If profile fetch fails, treat as new user
            profileInfo = { body: null };
          }

          // If user doesn't exist in database, register them.
          // Legacy /fan-profile/profile/:userID returns null when the user is
          // unknown; an existing user always has userID set. Don't use
          // brandID — the Groups LEFT JOIN shadows it to null for users who
          // aren't in any groups.
          if (!profileInfo.body?.userID) {
            const resolvedBrand = getHubBrandName() || brandName || param;
            const inviteTag = localStorage.getItem('inviteTag');

            if (resolvedBrand) {
              if (!getHubBrand().brandName) {
                getHubBrand().updateHubBrand({ brandName: resolvedBrand });
              }
              const brandCode = await lookupBrandCode(resolvedBrand, token);

              if (brandCode) {
                const reg = await registerFanUser({
                  userID,
                  email: user.email,
                  phoneNumber: user.phone_number,
                  picture: user.picture,
                  firstName: user.given_name || brandTitle,
                  lastName: user.family_name || '',
                  brandCode,
                  inviteTag,
                });

                if (!reg.ok) {
                  // On a signup-guard block the SignupStatusScreen is already
                  // showing; logging out would clear it. Only logout on a
                  // genuine (non-block) failure.
                  if (!reg.blocked) {
                    logger.error(new Error('Failed to register user'));
                    logout();
                  }
                  return;
                }
                setNeedsOnboarding(true);
              }
            }
          }
        } catch (_error) {
          // Clear tokens and auth state on error
          localStorage.removeItem('access_token');
          localStorage.removeItem('auth_in_progress');
          logout();
          return;
        }
      }

      // Check for existing auth - now requires token
      if (localStorage.getItem('access_token') && isAuthenticated && user) {
        try {
          // Verify token is still valid (authenticated only)
          const token = await fetchToken();
          if (!token) {
            try {
              if (typeof window !== 'undefined') {
                window.sessionStorage.setItem('force_interactive_login', '1');
              }
            } catch (_) {
              /* intentionally ignore sessionStorage access errors */
            }
            setIsLoading(true);
            await loginWithSSO(brandName, { ssoConnectionName: brand?.brandConfig?.ssoConnectionName });
            return;
          }

          // Verify the user exists in the database
          const userID = getUserIdentifier(user);
          let profileInfo;

          try {
            profileInfo = await listProfile(userID, token);
          } catch (_error) {
            // If profile fetch fails, treat as new user
            profileInfo = { body: null };
          }

          // If user doesn't exist in database, register them.
          // Legacy /fan-profile/profile/:userID returns null when the user is
          // unknown; an existing user always has userID set. Don't use
          // brandID — the Groups LEFT JOIN shadows it to null for users who
          // aren't in any groups.
          if (!profileInfo.body?.userID) {
            const resolvedBrand = getHubBrandName() || brandName || param;
            const inviteTag = localStorage.getItem('inviteTag');

            if (resolvedBrand) {
              if (!getHubBrand().brandName) {
                getHubBrand().updateHubBrand({ brandName: resolvedBrand });
              }
              const brandCode = await lookupBrandCode(resolvedBrand, token);

              if (brandCode) {
                const reg = await registerFanUser({
                  userID,
                  email: user.email,
                  phoneNumber: user.phone_number,
                  picture: user.picture,
                  firstName: user.given_name || brandTitle,
                  lastName: user.family_name || '',
                  brandCode,
                  inviteTag,
                });

                if (!reg.ok) {
                  // Block → SignupStatusScreen already shown; don't tear down
                  // auth. Only clear + logout on a genuine failure.
                  if (!reg.blocked) {
                    localStorage.removeItem('access_token');
                    localStorage.removeItem('auth_in_progress');
                    logout();
                  }
                  return;
                }
                setNeedsOnboarding(true);
              }
            }
          }
          return;
        } catch (_error) {
          logout();
          return;
        }
      }

      const isYubicoBrand = (brandName || '').toLowerCase() === 'yubico';

      // Detect a post-logout landing. The flag is set by markJustLoggedOut() in
      // AuthContext (after clearSessionState, so it survives the redirect). Only
      // consume it once both isSSOCheckComplete=true AND brandName is known — a
      // !brandName early-return sets isSSOCheckComplete=true before brand resolves,
      // which would strip the flag before the SSO-brand re-check sees it (ZLT-2936).
      if (isSSOCheckComplete && brandName && justLoggedOut) {
        clearJustLoggedOutFlag();
      }

      // justLoggedOut is consumed by the first render after a logout, so on its own it only holds
      // for one page load. The per-brand marker is what makes a refresh stay signed out, which
      // matters on an SSO brand where the identity provider session outlives an Auth0 logout.
      const shouldAutoLogin =
        !signInSuppressed &&
        (isSSOBrand || isYubicoBrand) &&
        !authAttempted &&
        !ssoNotConfigured &&
        !localStorage.getItem('access_token') &&
        !isAuthenticated &&
        brandNameDetermined &&
        brandName;

      if (shouldAutoLogin) {
        try {
          setIsLoading(true);
          setAuthAttempted(true);
          const ssoConnectionName = brand?.brandConfig?.ssoConnectionName;
          await loginWithSSO(brandName, { ssoConnectionName });
        } catch (error) {
          if (error.code === 'NO_SSO_CONFIGURED') {
            // Mark that SSO is not configured to prevent retry
            setSsoNotConfigured(true);
            setAuthAttempted(true);
          }
          localStorage.removeItem('access_token');
          localStorage.removeItem('auth_in_progress');
          localStorage.removeItem('login_method');
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleSSOAuth();

    return () => {
      if (!window.location.search.includes('code=')) {
        localStorage.removeItem('auth_in_progress');
      }
    };
  }, [
    isAuthenticated,
    auth0IsLoading,
    user,
    isLoading,
    brandName,
    authAttempted,
    loginWithSSO,
    logout,
    router.query.embed,
    brandNameDetermined,
    ssoNotConfigured,
    isSSOBrand, // ensure effect re-evaluates when SSO brand detection completes
    isSSOCheckComplete, // ensure flag is consumed only after SSO detection fully resolves
    normalizedProfile, // wait for claims to be loaded before registering users
  ]);

  // Separate useEffect for handling successful authentication
  useEffect(() => {
    const handleSuccessfulAuth = async () => {
      if (isAuthenticated && user) {
        // Only wait for normalizedProfile if user.email is not available
        // This handles SAML SSO users where email is only in ID token claims
        if (!normalizedProfile && !user.email) {
          return;
        }

        try {
          // Require an authenticated token for post-auth navigation
          const token = await fetchToken();
          if (!token) {
            // localStorage.removeItem("auth_in_progress");
            // logout();
            return;
          }

          if (user) {
            localStorage.setItem('user', JSON.stringify(user));
          }

          // Clear the in-progress flag
          localStorage.removeItem('auth_in_progress');

          // IdP-initiated re-trigger succeeded — clear the one-shot loop guard.
          window.sessionStorage.removeItem(IDP_RETRY_KEY);

          // Check if user has a profile
          const userID = getUserIdentifier(user);
          const profileInfo = await listProfile(userID, token);
          const noProfile = !profileInfo.body?.userID;
          const stillNeedsOnboarding = noProfile || isFirstTimeSignUp(profileInfo);

          // Navigate based on whether the user still needs onboarding.
          // /Onboarding requires ?signup=true (Onboarding.jsx bounces the
          // user to /HomeDesktop without it — added in #730 as a guard
          // against direct nav). The non-SSO paths above already pass it;
          // SSO falls through to here, so it must pass it too or new SSO
          // users get bounced past onboarding.
          if (stillNeedsOnboarding) {
            await navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
          } else {
            await navigateWithBrand(router, isMobileViewport() ? '/Home' : '/HomeDesktop');
          }
        } catch (error) {
          logger.error(error, { context: 'Auth completion error' });
        }
      }
    };

    handleSuccessfulAuth();
  }, [isAuthenticated, user, router, fetchToken, brandName, normalizedProfile]);

  // Shared loading spinner
  const LoadingSpinner = () => (
    <div className="fixed inset-0 w-screen h-screen bg-white flex flex-col items-center justify-center">
      {brandAssetsReady && loaderGifUrl ? (
        <motion.img
          src={loaderGifUrl}
          alt="Loading..."
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />
      ) : (
        <motion.div
          className="flex flex-col items-center gap-4"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="w-10 h-10 border-3 border-gray-200 border-t-gray-600 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
        </motion.div>
      )}
      {showResetButton && (
        <button onClick={handleReset} className="mt-4 text-gray-500 underline text-sm hover:text-gray-700">
          not loading?
        </button>
      )}
    </div>
  );

  // Phase 0-2: Loading states (include SSO check so we don't flash the login form before knowing if SSO applies)
  if (
    isLoading ||
    !brandNameDetermined ||
    (brandNameDetermined && brandName && (!brandAssetsReady || brandIsLoading || !isSSOCheckComplete))
  ) {
    return <LoadingSpinner />;
  }

  // SSO brand: show loader while SSO auto-triggers.
  // Skip when justLoggedOut: auto-login was suppressed, so the loader would
  // spin forever if we kept this gate open (ZLT-2936).
  if (brandNameDetermined && brandAssetsReady && brandName && isSSOBrand && !isLoading && !signInSuppressed) {
    return <LoadingSpinner />;
  }

  // Phase 3: Ready for login UI
  if (brandNameDetermined && brandAssetsReady && brandName) {
    // Shared login handler for email/password
    const handleLogin = async () => {
      setLoginError('');
      setLoginInProgress(true);
      if (isSignUp) {
        // Hold the spinner through the route transition; release it only if we stayed here.
        let navigated = false;
        try {
          navigated = await handleEmailPasswordSignUp();
        } finally {
          if (!navigated) setLoginInProgress(false);
        }
        return;
      }
      try {
        await assertFanUserNotBanned(apiClient, email);
        const { accessToken, idToken } = await loginWithEmailAndPassword(email, verificationCode);
        localStorage.removeItem('signupEmail');
        if (!idToken || !persistIdentityFromIdToken(idToken, email)) {
          setLoginInProgress(false);
          setLoginError('Session error. Please try again.');
          return;
        }
        const resolvedBrand = getHubBrandName() || brandName || param;
        const inviteTag = localStorage.getItem('inviteTag');
        if (resolvedBrand && accessToken) {
          if (!getHubBrand().brandName) {
            getHubBrand().updateHubBrand({ brandName: resolvedBrand });
          }
          try {
            const brandCode = await lookupBrandCode(resolvedBrand, accessToken);
            if (brandCode) {
              const reg = await registerFanUser({
                userID: localStorage.getItem('userID'),
                email,
                brandCode,
                inviteTag,
              });
              // Blocked → SignupStatusScreen is already shown; stop before navigating away.
              if (reg.blocked) {
                setLoginInProgress(false);
                return;
              }
            }
          } catch (_e) {
            /* intentional */
          }
        }
        let pInfo = null;
        try {
          pInfo = await listProfile(localStorage.getItem('userID'), accessToken);
        } catch (e) {
          // Transient listProfile failure: we can't tell whether the user is onboarded.
          // Routing to onboarding is the safer default for the email/password sign-up path
          // (where this code lives), but we log so we can spot existing users getting bounced.
          logger.warn(e, { context: 'handleLogin: listProfile failed; defaulting to onboarding' });
        }
        const stillNeedsOnboarding = !pInfo?.body?.userID || isFirstTimeSignUp(pInfo);
        if (stillNeedsOnboarding) {
          navigateWithBrand(router, '/Onboarding', { query: { signup: 'true' } });
          return;
        }
        navigateWithBrand(router, isMobileViewport() ? '/Home' : '/HomeDesktop');
      } catch (e) {
        setLoginInProgress(false);
        if (!(e instanceof FanUserBannedError)) setLoginError(e.message || 'Login failed');
      }
    };

    return (
      <>
        <Head>
          <title>{brandTitle || brandName}</title>
          {(favicon || brand?.brandConfig?.icon || brandLogo) && (
            <link rel="icon" href={favicon || brand?.brandConfig?.icon || brandLogo} />
          )}
        </Head>
        <AuthLayout>
          <div className="flex w-full max-w-[440px] flex-col gap-6 rounded-[20px] bg-white p-5 md:p-8 md:shadow-auth-card md:ring-1 md:ring-gray-100">
            {/* Header: icon + title */}
            <div className="flex flex-col items-center gap-2">
              {/* Gradient circle with user icon */}
              <div className="relative flex h-[68px] w-[68px] shrink-0 items-center justify-center rounded-full backdrop-blur-xl md:h-24 md:w-24 before:absolute before:inset-0 before:rounded-full before:bg-linear-to-b before:from-neutral-500 before:to-transparent before:opacity-10">
                <div className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-xs ring-1 ring-inset ring-gray-200 md:h-16 md:w-16">
                  <svg className="h-6 w-6 text-gray-500 md:h-8 md:w-8" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                </div>
              </div>

              <div className="space-y-1 text-center">
                <div className="text-lg font-semibold text-gray-900 md:text-xl">
                  {isSignUp ? 'Create your account' : 'Login to your account'}
                </div>
                <div className="text-sm text-gray-500 md:text-base">
                  {isSignUp ? 'Enter your details to sign up.' : 'Enter your details to login.'}
                </div>
              </div>
            </div>

            {/* Approved-applicant banner. Shown when the user arrives from the
                application-approved email link (?signup=true&email=…). Purely
                informational — the backend is the authority on approval. */}
            {isSignUp && router.query.signup === 'true' && router.query.email && (
              <div className="flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                <svg className="mt-0.5 h-4 w-4 shrink-0 text-green-500" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                </svg>
                <p className="text-sm text-green-700">
                  Your application{brand?.brandName ? ` to ${brand.brandName}` : ''} was approved — create your password
                  to get started.
                </p>
              </div>
            )}

            {/* Divider */}
            <div className="h-px w-full bg-gray-200" />

            {/* Form */}
            <AnimatePresence mode="wait">
                <motion.div
                  key="password-form"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="flex flex-col gap-4"
                >
                  {/* Email field */}
                  <div className="flex flex-col gap-1">
                    <label htmlFor="login-email" className="text-sm font-medium text-gray-700">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="group relative flex items-center rounded-lg border border-gray-200 bg-white transition-colors focus-within:border-gray-400 hover:border-gray-300">
                      <div className="pointer-events-none flex items-center pl-3">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="2" y="4" width="20" height="16" rx="3" />
                          <path d="m2 7 10 6 10-6" />
                        </svg>
                      </div>
                      <input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="hello@example.com"
                        className="w-full bg-transparent py-2.5 pl-2.5 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-hidden"
                      />
                    </div>
                  </div>

                  {/* Password field */}
                  <div className="flex flex-col gap-1">
                    <label htmlFor="login-password" className="text-sm font-medium text-gray-700">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <div className="group relative flex items-center rounded-lg border border-gray-200 bg-white transition-colors focus-within:border-gray-400 hover:border-gray-300">
                      <div className="pointer-events-none flex items-center pl-3">
                        <svg
                          className="h-5 w-5 text-gray-400"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="11" width="18" height="11" rx="2" />
                          <path d="M7 11V7a5 5 0 0110 0v4" />
                        </svg>
                      </div>
                      <input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value)}
                        placeholder="••••••••••"
                        className="w-full bg-transparent py-2.5 pl-2.5 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:outline-hidden"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((s) => !s)}
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showPassword ? (
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
                            />
                          </svg>
                        ) : (
                          <svg
                            className="h-5 w-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            strokeWidth={1.5}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                            />
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                        )}
                      </button>
                    </div>
                    {/* Single failing rule, shown live below the password field (signup only). */}
                    {isSignUp && verificationCode && firstPasswordHint(verificationCode) ? (
                      <p className="mt-1 text-xs text-red-600">{firstPasswordHint(verificationCode)}</p>
                    ) : null}
                  </div>

                  {/* Confirm password (signup only) */}
                  <AnimatePresence mode="wait">
                    {isSignUp && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="flex flex-col gap-1 overflow-hidden"
                      >
                        <label htmlFor="login-confirm" className="text-sm font-medium text-gray-700">
                          Confirm Password <span className="text-red-500">*</span>
                        </label>
                        <div className="group relative flex items-center rounded-lg border border-gray-200 bg-white transition-colors focus-within:border-gray-400 hover:border-gray-300">
                          <div className="pointer-events-none flex items-center pl-3">
                            <svg
                              className="h-5 w-5 text-gray-400"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth={1.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <rect x="3" y="11" width="18" height="11" rx="2" />
                              <path d="M7 11V7a5 5 0 0110 0v4" />
                            </svg>
                          </div>
                          <input
                            id="login-confirm"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="••••••••••"
                            className="w-full bg-transparent py-2.5 pl-2.5 pr-4 text-sm text-gray-900 placeholder-gray-400 focus:outline-hidden"
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* One static policy hint below the password fields (signup only). */}
                  {isSignUp && <p className="text-xs text-gray-400">{PASSWORD_POLICY_HINT}</p>}

                  {/* Success message */}
                  <AnimatePresence>
                    {loginSuccess && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="rounded-lg bg-green-50 border border-green-200 px-3 py-2"
                      >
                        <p className="text-green-600 text-sm text-center">{loginSuccess}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Error message */}
                  <AnimatePresence>
                    {loginError && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -5 }}
                        className="rounded-lg bg-red-50 border border-red-200 px-3 py-2"
                      >
                        <p className="text-red-600 text-sm text-center">{loginError}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Forgot password */}
                  {!isSignUp && (
                    <div className="flex w-full items-center justify-end mt-10">
                      <button
                        onClick={() => {
                          navigateWithBrand(router, '/forgot-password');
                        }}
                        className="text-gray-500 text-sm font-medium underline underline-offset-2 hover:no-underline transition-colors"
                      >
                        Forgot password?
                      </button>
                    </div>
                  )}

                  {/* Submit button */}
                  <div className="mt-2">
                    <TextureButton
                      size="lg"
                      onClick={handleLogin}
                      disabled={loginInProgress}
                      className={`cursor-pointer ${loginInProgress ? 'opacity-80' : ''}`}
                    >
                      {loginInProgress ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                          {isSignUp ? 'Signing up...' : 'Logging in...'}
                        </div>
                      ) : isSignUp ? (
                        'Sign up'
                      ) : (
                        'Login'
                      )}
                    </TextureButton>
                  </div>

                  {/* Toggle sign in / sign up */}
                  <div className="text-gray-500 text-sm flex flex-wrap items-center justify-center gap-1">
                    {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
                    <button
                      onClick={() => setIsSignUp(!isSignUp)}
                      className="text-gray-700 text-sm font-medium underline underline-offset-2 hover:no-underline"
                    >
                      {isSignUp ? 'Sign in' : 'Sign up'}
                    </button>
                  </div>
                </motion.div>
            </AnimatePresence>
          </div>
        </AuthLayout>
      </>
    );
  }

  // Skip brand validation fallback for embed mode
  if (router.query.embed === 'true') {
    return (
      <div className="min-w-screen min-h-screen bg-white flex flex-col items-center justify-center">
        <p className="text-lg text-gray-700">Loading...</p>
      </div>
    );
  }

  // Fallback
  // Second gate, defense-in-depth: the API's requireNonProductionEnvMiddleware
  // already 404s /brands/picker in production, but gating the FE too keeps the
  // picker from mounting (and firing a doomed request) there. Available in
  // every other environment (local, develop, staging, Vercel review apps) so
  // engineers and QA can pick a brand without hand-crafting a URL.
  const isBrandPickerEnabled = process.env.NEXT_PUBLIC_ENVIRONMENT !== 'production';

  return (
    <div className="min-w-screen min-h-screen bg-white flex flex-col items-center justify-center">
      <p className="text-lg text-gray-700">Please specify a brand in the URL.</p>
      {isBrandPickerEnabled && <BrandPicker />}
    </div>
  );
};

export default Index;
